// Custom Action page — Zoho CRM → Shopify outbound mapping wizard.
//
// Initialised the same way as RelatedList.js (initZoho, gate on
// shopifyAuth + zohoAuth). When both auths pass, render the 4-step wizard:
//   1. Zoho App     — Zoho CRM is fixed; Module is taken from initZoho.
//   2. Shopify      — Shop + Target Resource pickers.
//   3. Field Mapping — Shopify (left) ← Zoho (right) with type filtering,
//                       lookup capture, subform expansion, default-value rows.
//   4. Actions      — Record action (create/upsert/update/delete) +
//                       Match Field (Zoho field that stores the Shopify ID).
//
// On save:
//   - upsertSetting writes the schema to the settings DB and returns settingId
//   - ZOHO.CRM.ACTION.setConfig stores that settingId against the current
//     Custom Action button so a return visit can load the saved config.
// On load:
//   - ZOHO.CRM.ACTION.getConfig is read; if it has a settingId, the existing
//     setting is fetched and the wizard is prefilled for editing.

import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import LinkIcon from "@mui/icons-material/Link";
import LockIcon from "@mui/icons-material/Lock";
import PushPinIcon from "@mui/icons-material/PushPin";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

import { Loader } from "../components/atoms/Loader";
import ShowError from "../components/atoms/ShowError";
import { initZohoCustomAction } from "../util/zohoUtil";
import { getZohoModuleFields } from "../APICalling/zohoAdminApi";
import { upsertSetting } from "../APICalling/settingApi";
import { isShopifyCompatibleWithZoho } from "../util/typeCompat";
import { EXTENSION_IDENTIFIER } from "../config/serviceConstant";
import SHOPIFY_FIELDS_MAP from "../config/shopifyFields";

const ZOHO = window.ZOHO;
const EXT = EXTENSION_IDENTIFIER;

// ── Step labels ──
const STEPS = ["Zoho App", "Shopify", "Field Mapping", "Actions"];

// Map a Zoho CRM module to the most common Shopify target. Only used as the
// initial pre-selection for the Shopify step; user can change it.
const DEFAULT_TARGET_BY_MODULE = {
  Contacts: "customers",
  Accounts: "customers",
  Sales_Orders: "orders",
  Products: "products",
};

// Zoho module → Shopify resource KEY in SHOPIFY_FIELDS_MAP. Allows us to swap
// from "customers" / "orders" / "products" (the wizard's `target` value, used
// in API URLs) to the corresponding capitalised key in SHOPIFY_FIELDS_MAP.
const SHOPIFY_FIELDS_MAP_KEY = {
  customers: "Customers",
  orders: "Orders",
  products: "Products",
  collections: "Collections",
  fulfillments: "Fulfillments",
  refunds: "Refunds",
  draft_orders: "Draft Orders",
  inventory_items: "Inventory Items",
};

// ── Helpers ──
const EMPTY_ROW = () => ({
  Shopify: "",
  Zoho: "",
  default: false,
  defaultValue: "",
  locked: false,
  subRows: [],
  lookupModuleApiName: "",
  shopifyType: "",
  zohoType: "",
});

const isExtensionOwnedZohoField = (apiName) =>
  typeof apiName === "string" && apiName.startsWith(`${EXT}__`);

// Hardcoded fallback for well-known built-in subforms whose sub-fields Zoho's
// getFields endpoint doesn't always expose. Keyed by parent api_name.
const HARDCODED_SUBFORM_SUBFIELDS = {
  Product_Details: [
    {
      api_name: "product",
      display_label: "Product",
      data_type: "lookup",
      lookup: { module: { api_name: "Products" } },
      system_mandatory: true,
    },
    { api_name: "quantity", display_label: "Quantity", data_type: "double", system_mandatory: true },
    { api_name: "list_price", display_label: "List Price", data_type: "currency" },
    { api_name: "unit_price", display_label: "Unit Price", data_type: "currency" },
    { api_name: "Discount", display_label: "Discount", data_type: "currency" },
    { api_name: "total", display_label: "Total", data_type: "currency" },
    { api_name: "total_after_discount", display_label: "Total After Discount", data_type: "currency" },
    { api_name: "net_total", display_label: "Net Total", data_type: "currency" },
    { api_name: "Tax", display_label: "Tax", data_type: "double" },
    { api_name: "description", display_label: "Description", data_type: "textarea" },
    { api_name: "quantity_in_stock", display_label: "Quantity in Stock", data_type: "double" },
    { api_name: "product_description", display_label: "Product Description", data_type: "textarea" },
  ],
};

const isSubformLikeZohoField = (zohoField) => {
  if (!zohoField) return false;
  const dt = zohoField.data_type;
  if (dt === "subform" || dt === "list") return true;
  // Some Zoho responses return Product_Details with a non-standard data_type
  // (sometimes empty). Fall back to api_name match.
  const apiName = zohoField.api_name || "";
  return Object.prototype.hasOwnProperty.call(
    HARDCODED_SUBFORM_SUBFIELDS,
    apiName
  );
};

// Pull the subform sub-module API name + any inline sub-field metadata out of
// a Zoho field record. Zoho returns this in different shapes depending on
// whether the field is a custom subform or a built-in list (e.g. Deals'
// Product_Details). Falls back to the hardcoded shape for well-known fields.
const getSubformContext = (zohoField) => {
  if (!isSubformLikeZohoField(zohoField)) return null;
  const moduleApiName =
    zohoField?.subform?.module?.api_name ||
    (typeof zohoField?.subform?.module === "string"
      ? zohoField.subform.module
      : "") ||
    zohoField?.subform_module ||
    "";
  const inlineFields =
    (Array.isArray(zohoField?.subform?.fields) && zohoField.subform.fields) ||
    (Array.isArray(zohoField?.fields) && zohoField.fields) ||
    [];
  const fallbackFields = HARDCODED_SUBFORM_SUBFIELDS[zohoField.api_name] || [];
  return { moduleApiName, inlineFields, fallbackFields };
};

const isIdSubRow = (sr) =>
  typeof sr?.Shopify === "string" && /\[\]\.id$/.test(sr.Shopify);

const filterShopifyArraySubFields = (shopifyFields, parentApiName) => {
  if (!parentApiName) return [];
  const prefix = `${parentApiName}[].`;
  return shopifyFields.filter((f) => (f.api_name || f).startsWith(prefix));
};

const normaliseRow = (r) => {
  if (!r) return EMPTY_ROW();
  return {
    ...EMPTY_ROW(),
    Shopify: r.Shopify ?? r.shopify ?? "",
    Zoho: r.Zoho ?? r.zoho ?? "",
    default: r.default === true,
    defaultValue: r.defaultValue ?? r.default_value ?? "",
    locked:
      r.locked === true ||
      isExtensionOwnedZohoField(r.Zoho ?? r.zoho ?? ""),
    subRows: Array.isArray(r.subRows)
      ? r.subRows.map(normaliseRow)
      : [],
    lookupModuleApiName:
      r.lookupModuleApiName ?? r.lookup_module_api_name ?? "",
    shopifyType: r.shopifyType ?? r.shopify_type ?? "",
    zohoType: r.zohoType ?? r.zoho_type ?? "",
  };
};

// ── Saved-setting banner ──
// Renders the persisted settingId at the top of the edit view with a
// click-to-copy button. Only shown once the setting has been resolved (i.e.
// the user is editing an existing Custom Action config).
function SavedSettingBanner({ settingId }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(settingId);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      // Older browsers / restricted clipboard contexts — silently swallow.
    }
  };
  return (
    <Box
      sx={{
        maxWidth: 900,
        mx: "auto",
        mt: 4,
        mb: -2, // pulls the wizard card up so the gap is uniform
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1.5,
        px: 2,
        py: 1.25,
        bgcolor: "#fff",
        border: "1px dashed #c7d2fe",
        borderRadius: "8px",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
        <Typography
          sx={{ fontSize: 12, fontWeight: 600, color: "#4338ca", textTransform: "uppercase", letterSpacing: "0.06em" }}
        >
          Setting&nbsp;ID
        </Typography>
        <Typography
          sx={{
            fontSize: 14,
            fontFamily: "monospace",
            color: "#1e293b",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {settingId}
        </Typography>
      </Box>
      <Tooltip title={copied ? "Copied" : "Copy to clipboard"}>
        <IconButton
          size="small"
          onClick={onCopy}
          sx={{ color: copied ? "#16a34a" : "#4338ca" }}
        >
          {copied ? (
            <CheckIcon sx={{ fontSize: 17 }} />
          ) : (
            <ContentCopyIcon sx={{ fontSize: 17 }} />
          )}
        </IconButton>
      </Tooltip>
    </Box>
  );
}

// ── Stepper ──
// `maxReachable` is the highest step index the user is allowed to jump to
// (calculated from the parent's per-step validation). Steps beyond it render
// disabled and ignore clicks.
function StepIndicator({ currentStep, onStepClick, maxReachable = STEPS.length - 1 }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", px: 3.5, pt: 2.5, pb: 2 }}>
      {STEPS.map((step, i) => {
        const reachable = i <= maxReachable;
        return (
        <React.Fragment key={step}>
          <Box
            onClick={reachable ? () => onStepClick(i) : undefined}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexShrink: 0,
              cursor: reachable ? "pointer" : "not-allowed",
              opacity: reachable ? 1 : 0.45,
              "&:hover": reachable ? { opacity: 0.8 } : {},
            }}
          >
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 13,
                fontWeight: 600,
                border: "1.5px solid",
                borderColor:
                  i < currentStep ? "#22c55e" : i === currentStep ? "#09090b" : "#e4e4e7",
                bgcolor:
                  i < currentStep ? "#22c55e" : i === currentStep ? "#09090b" : "#fff",
                color: i <= currentStep ? "#fff" : "#71717a",
                transition: "all 0.2s",
              }}
            >
              {i < currentStep ? "✓" : i + 1}
            </Box>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: i === currentStep ? 600 : 500,
                color:
                  i < currentStep ? "#16a34a" : i === currentStep ? "#09090b" : "#71717a",
                whiteSpace: "nowrap",
              }}
            >
              {step}
            </Typography>
          </Box>
          {i < STEPS.length - 1 && (
            <Box
              sx={{
                flex: 1,
                height: "1.5px",
                bgcolor: i < currentStep ? "#22c55e" : "#e4e4e7",
                mx: 1.25,
                transition: "background 0.2s",
              }}
            />
          )}
        </React.Fragment>
        );
      })}
    </Box>
  );
}

// ── Step 1: Zoho App (CRM fixed, Module from initZoho) ──
function ZohoAppStep({ moduleName }) {
  return (
    <Box sx={{ p: 3.5, pt: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Box sx={{ fontSize: 18 }}>&#x1F517;</Box>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#09090b" }}>
          Zoho CRM Configuration
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 14, color: "#71717a", mb: 2.5 }}>
        The Zoho app and module for this custom action are taken from the
        current Zoho record context.
      </Typography>

      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#3f3f46", mb: "6px" }}>
          App
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            border: "1px solid #e4e4e7",
            borderRadius: "6px",
            px: 1.5,
            py: 1,
            bgcolor: "#f4f4f5",
            color: "#3f3f46",
            fontSize: 14,
          }}
        >
          <Box
            sx={{
              width: 22,
              height: 22,
              borderRadius: "5px",
              bgcolor: "#eff6ff",
              color: "#1d4ed8",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            Z
          </Box>
          Zoho CRM
          <Chip
            label="Fixed"
            size="small"
            sx={{ ml: "auto", height: 20, fontSize: 11, bgcolor: "#e4e4e7", color: "#71717a" }}
          />
        </Box>
      </Box>

      <Box>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#3f3f46", mb: "6px" }}>
          Module
        </Typography>
        <TextField
          size="small"
          fullWidth
          value={moduleName || ""}
          disabled
          inputProps={{ style: { fontSize: 14 } }}
        />
        <Typography sx={{ fontSize: 12, color: "#a1a1aa", mt: "4px" }}>
          Pre-selected from the current Zoho record context.
        </Typography>
      </Box>
    </Box>
  );
}

// ── Step 2: Shopify ──
function ShopifyStep({ shop, setShop, target, setTarget, stores }) {
  return (
    <Box sx={{ p: 3.5, pt: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Box sx={{ fontSize: 18 }}>&#x1F6CD;&#xFE0F;</Box>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#09090b" }}>
          Shopify Configuration
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 14, color: "#71717a", mb: 2.5 }}>
        Pick the destination Shopify store and the resource to create or update.
      </Typography>

      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#3f3f46", mb: "6px" }}>
          Shop Name
        </Typography>
        <FormControl fullWidth size="small">
          <Select
            value={shop}
            onChange={(e) => setShop(e.target.value)}
            displayEmpty
            sx={{ fontSize: 14 }}
          >
            <MenuItem value="" disabled sx={{ fontSize: 14 }}>
              — Select shop —
            </MenuItem>
            {stores.map((s) => {
              const dom = s.domain || s.storeDomain || s.name || "";
              return (
                <MenuItem key={dom} value={dom} sx={{ fontSize: 14 }}>
                  {dom}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </Box>

      <Box>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#3f3f46", mb: "6px" }}>
          Target Resource
        </Typography>
        <FormControl fullWidth size="small">
          <Select
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            displayEmpty
            sx={{ fontSize: 14 }}
          >
            <MenuItem value="" disabled sx={{ fontSize: 14 }}>
              — Select target —
            </MenuItem>
            <MenuItem value="customers" sx={{ fontSize: 14 }}>Customer</MenuItem>
            <MenuItem value="orders" sx={{ fontSize: 14 }}>Order</MenuItem>
            <MenuItem value="products" sx={{ fontSize: 14 }}>Product</MenuItem>
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
}

// ── A single mapping row (Shopify left, Zoho right) ──
function MappingRow({
  row,
  index,
  shopifyFields,
  zohoFields,
  allRows,
  onChange,
  onSourceChange,
  onAdd,
  onRemove,
  isRequired,
  isLocked,
}) {
  const isDefault = row.default === true;
  const usedShopify = allRows
    .map((r, i) => (i !== index && r.default !== true ? r.Shopify : null))
    .filter(Boolean);
  const usedZoho = allRows
    .map((r, i) => (i !== index ? r.Zoho : null))
    .filter(Boolean);

  const selectedZohoField = zohoFields.find(
    (f) => (f.api_name || f) === row.Zoho
  );
  const selectedShopifyField = shopifyFields.find(
    (f) => (f.api_name || f) === row.Shopify
  );

  const availableShopify = shopifyFields.filter((f) => {
    const apiName = f.api_name || f;
    if (usedShopify.includes(apiName)) return false;
    if (apiName === row.Shopify) return true;
    if (!selectedZohoField) return true;
    return isShopifyCompatibleWithZoho(
      f.type,
      selectedZohoField.data_type,
      selectedZohoField.api_name,
      apiName
    );
  });
  const availableZoho = zohoFields.filter((f) => {
    const apiName = f.api_name || f;
    if (usedZoho.includes(apiName)) return false;
    if (apiName === row.Zoho) return true;
    if (isDefault || !selectedShopifyField) return true;
    return isShopifyCompatibleWithZoho(
      selectedShopifyField.type,
      f.data_type,
      apiName,
      selectedShopifyField.api_name || selectedShopifyField
    );
  });

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr 60px 1fr 100px",
        alignItems: "center",
        gap: 1,
        mb: 1,
      }}
    >
      {/* LEFT: Shopify (target) */}
      <Autocomplete
        size="small"
        disabled={isLocked}
        options={availableShopify}
        getOptionLabel={(opt) => opt.api_name || opt || ""}
        value={
          shopifyFields.find((f) => (f.api_name || f) === row.Shopify) || null
        }
        onChange={(_, newVal) =>
          onChange(index, "Shopify", newVal ? newVal.api_name || newVal : "")
        }
        isOptionEqualToValue={(opt, val) =>
          (opt.api_name || opt) === (val.api_name || val)
        }
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="— Shopify field —"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {selectedShopifyField?.type && (
                    <Chip
                      label={selectedShopifyField.type}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: 11,
                        bgcolor: "#f0fdf4",
                        color: "#15803d",
                        mr: 0.5,
                      }}
                    />
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />

      {/* MIDDLE: source mode toggle (link / pin / lock) + arrow */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
        <Tooltip
          title={
            isLocked
              ? "Locked extension field"
              : isDefault
              ? "Default value (literal)"
              : "Linked Zoho field"
          }
        >
          <span>
            <IconButton
              size="small"
              disabled={isLocked || isRequired}
              onClick={() => onSourceChange(index, !isDefault)}
              sx={{
                border: "1.5px solid",
                borderColor: isLocked ? "#e4e4e7" : isDefault ? "#f59e0b" : "#a1a1aa",
                color: isLocked ? "#a1a1aa" : isDefault ? "#f59e0b" : "#71717a",
                width: 26,
                height: 26,
              }}
            >
              {isLocked ? (
                <LockIcon sx={{ fontSize: 15 }} />
              ) : isDefault ? (
                <PushPinIcon sx={{ fontSize: 15 }} />
              ) : (
                <LinkIcon sx={{ fontSize: 15 }} />
              )}
            </IconButton>
          </span>
        </Tooltip>
        <Box sx={{ color: "#71717a", fontSize: 15 }}>&larr;</Box>
      </Box>

      {/* RIGHT: Zoho (source) — either a field picker or a literal value */}
      {isDefault ? (
        <TextField
          size="small"
          placeholder="Default value"
          value={row.defaultValue || ""}
          onChange={(e) => onChange(index, "defaultValue", e.target.value)}
          disabled={isLocked}
          fullWidth
        />
      ) : (
        <Autocomplete
          size="small"
          disabled={isLocked}
          options={availableZoho}
          // Group by the module the field belongs to (main module + each
          // related lookup / subform). Falls back to no grouping when the
          // backend didn't send `_group` (legacy flat shape).
          groupBy={(opt) => opt._group || ""}
          getOptionLabel={(opt) =>
            opt.display_label || opt.api_name || opt || ""
          }
          value={
            zohoFields.find((f) => (f.api_name || f) === row.Zoho) || null
          }
          onChange={(_, newVal) =>
            onChange(index, "Zoho", newVal ? newVal.api_name || newVal : "")
          }
          isOptionEqualToValue={(opt, val) =>
            (opt.api_name || opt) === (val.api_name || val)
          }
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="— Zoho field —"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {selectedZohoField?.data_type === "lookup" && (
                      <Chip
                        label={`→ ${selectedZohoField.lookup?.module?.api_name || "?"}`}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: 11,
                          bgcolor: "#fef3c7",
                          color: "#a16207",
                          mr: 0.5,
                        }}
                      />
                    )}
                    {selectedZohoField?.data_type &&
                      selectedZohoField?.data_type !== "lookup" && (
                        <Chip
                          label={selectedZohoField.data_type}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: 11,
                            bgcolor: "#eff6ff",
                            color: "#1d4ed8",
                            mr: 0.5,
                          }}
                        />
                      )}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      )}

      {/* + / - row buttons */}
      <Box sx={{ display: "flex", gap: "4px" }}>
        <IconButton
          size="small"
          onClick={onAdd}
          disabled={isLocked}
          sx={{
            border: "1.5px solid",
            borderColor: isLocked ? "#e4e4e7" : "#22c55e",
            color: isLocked ? "#e4e4e7" : "#22c55e",
            width: 26,
            height: 26,
          }}
        >
          <AddIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => onRemove(index)}
          disabled={isRequired || isLocked}
          sx={{
            border: "1.5px solid",
            borderColor: isRequired || isLocked ? "#e4e4e7" : "#ef4444",
            color: isRequired || isLocked ? "#e4e4e7" : "#ef4444",
            width: 26,
            height: 26,
          }}
        >
          <RemoveIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
    </Box>
  );
}

// ── Subform block ──
function SubformBlock({
  parentRow,
  parentIndex,
  shopifyFields,
  subZohoFields,
  onSubChange,
  onSubSourceChange,
  onSubAdd,
  onSubRemove,
}) {
  const subShopifyFields = useMemo(
    () => filterShopifyArraySubFields(shopifyFields, parentRow.Shopify),
    [shopifyFields, parentRow.Shopify]
  );
  const subRows = parentRow.subRows?.length
    ? parentRow.subRows
    : [EMPTY_ROW()];

  return (
    <Box
      sx={{
        ml: 4,
        mr: 1,
        mb: 1.5,
        borderLeft: "2px solid #c7d2fe",
        bgcolor: "#fafbff",
        borderRadius: "6px",
        px: 1.5,
        py: 1.25,
      }}
    >
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 600,
          color: "#4338ca",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          mb: 1,
          display: "flex",
          alignItems: "center",
          gap: "6px",
        }}
      >
        <Box component="span">↳</Box>
        Subform fields ({parentRow.Shopify || "?"} ← {parentRow.Zoho || "?"})
      </Typography>
      {subRows.map((sr, j) => (
        <MappingRow
          key={j}
          row={sr}
          index={j}
          shopifyFields={subShopifyFields}
          zohoFields={subZohoFields}
          allRows={subRows}
          onChange={(idx, key, val) => onSubChange(parentIndex, idx, key, val)}
          onSourceChange={(idx, isDefault) =>
            onSubSourceChange(parentIndex, idx, isDefault)
          }
          onAdd={() => onSubAdd(parentIndex)}
          onRemove={(idx) => onSubRemove(parentIndex, idx)}
          isRequired={isIdSubRow(sr)}
          isLocked={false}
        />
      ))}
    </Box>
  );
}

// ── Step 3: Field Mapping ──
function FieldMappingStep({
  moduleName,
  shop,
  target,
  rows,
  setRows,
  zohoFields,
  shopifyFields,
  zohoFieldsLoading,
  subformFieldsCache,
  ensureSubformLoaded,
}) {
  const onChange = (idx, key, val) => {
    setRows((prev) => {
      const next = [...prev];
      const cur = { ...EMPTY_ROW(), ...next[idx], [key]: val };

      if (key === "Zoho") {
        const zf = zohoFields.find((f) => (f.api_name || f) === val);
        cur.lookupModuleApiName =
          zf?.data_type === "lookup"
            ? zf.lookup?.module?.api_name || ""
            : "";
        if (cur.Shopify) {
          const sf = shopifyFields.find((f) => (f.api_name || f) === cur.Shopify);
          if (
            zf &&
            sf &&
            !isShopifyCompatibleWithZoho(sf.type, zf.data_type, val, cur.Shopify)
          ) {
            cur.Shopify = "";
          }
        }
        if (!isSubformLikeZohoField(zf)) cur.subRows = [];
        else ensureSubformLoaded(zf);
      }

      if (key === "Shopify") {
        const sf = shopifyFields.find((f) => (f.api_name || f) === val);
        if (sf?.type === "array") {
          const idPath = `${val}[].id`;
          const hasIdRow = (cur.subRows || []).some((sr) => sr.Shopify === idPath);
          const subs = (cur.subRows || []).filter(
            (sr) => !isIdSubRow(sr) || sr.Shopify === idPath
          );
          if (!hasIdRow && shopifyFields.find((f) => (f.api_name || f) === idPath)) {
            subs.push({ ...EMPTY_ROW(), Shopify: idPath });
          }
          cur.subRows = subs;
        } else {
          cur.subRows = [];
        }
      }

      next[idx] = cur;
      return next;
    });
  };

  const onSourceChange = (idx, isDefault) => {
    setRows((prev) => {
      const next = [...prev];
      next[idx] = {
        ...EMPTY_ROW(),
        ...next[idx],
        default: isDefault,
        Zoho: isDefault ? "" : next[idx].Zoho,
        defaultValue: isDefault ? next[idx].defaultValue || "" : "",
        subRows: isDefault ? [] : next[idx].subRows || [],
      };
      return next;
    });
  };

  const onAdd = () => setRows((prev) => [...prev, EMPTY_ROW()]);
  const onRemove = (idx) =>
    setRows((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));

  const onSubChange = (parentIdx, subIdx, key, val) => {
    setRows((prev) => {
      const next = [...prev];
      const parent = { ...next[parentIdx] };
      const subs = [...(parent.subRows || [])];
      subs[subIdx] = { ...EMPTY_ROW(), ...subs[subIdx], [key]: val };
      next[parentIdx] = { ...parent, subRows: subs };
      return next;
    });
  };
  const onSubSourceChange = (parentIdx, subIdx, isDefault) => {
    setRows((prev) => {
      const next = [...prev];
      const parent = { ...next[parentIdx] };
      const subs = [...(parent.subRows || [])];
      subs[subIdx] = {
        ...EMPTY_ROW(),
        ...subs[subIdx],
        default: isDefault,
        Zoho: isDefault ? "" : subs[subIdx].Zoho,
      };
      next[parentIdx] = { ...parent, subRows: subs };
      return next;
    });
  };
  const onSubAdd = (parentIdx) => {
    setRows((prev) => {
      const next = [...prev];
      const parent = { ...next[parentIdx] };
      parent.subRows = [...(parent.subRows || []), EMPTY_ROW()];
      next[parentIdx] = parent;
      return next;
    });
  };
  const onSubRemove = (parentIdx, subIdx) => {
    setRows((prev) => {
      const next = [...prev];
      const parent = { ...next[parentIdx] };
      parent.subRows = (parent.subRows || []).filter((_, i) => i !== subIdx);
      next[parentIdx] = parent;
      return next;
    });
  };

  const summaryChips = [
    ["App", "Zoho CRM"],
    ["Module", moduleName],
    ["Shop", shop || "—"],
    ["Target", target || "—"],
  ];

  return (
    <Box sx={{ p: 3.5, pt: 2 }}>
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
        {summaryChips.map(([label, value]) => (
          <Box
            key={label}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              fontSize: 13,
              color: "#3f3f46",
              bgcolor: "#f4f4f5",
              border: "1px solid #e4e4e7",
              borderRadius: "6px",
              px: "10px",
              py: "5px",
            }}
          >
            <Box component="span" sx={{ fontWeight: 600 }}>
              {label}:
            </Box>{" "}
            {value}
          </Box>
        ))}
      </Box>

      {zohoFieldsLoading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 4, justifyContent: "center" }}>
          <CircularProgress size={20} />
          <Typography sx={{ fontSize: 14, color: "#71717a" }}>Loading fields...</Typography>
        </Box>
      ) : (
        <>
          {/* Column headers — grey bar */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 60px 1fr 100px",
              alignItems: "center",
              bgcolor: "#f4f4f5",
              borderTop: "1px solid #e4e4e7",
              borderBottom: "1px solid #e4e4e7",
              py: 1,
              borderRadius: "4px",
              mb: 1,
            }}
          >
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#52525b", display: "flex", alignItems: "center", gap: "6px", pl: 1.5 }}>
              <Box sx={{ width: 22, height: 22, borderRadius: "50%", bgcolor: "#16a34a", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700 }}>
                S
              </Box>
              Shopify Fields
            </Typography>
            <Box />
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#52525b", display: "flex", alignItems: "center", gap: "6px" }}>
              <Box sx={{ width: 22, height: 22, borderRadius: "50%", bgcolor: "#e0e7ff", color: "#4338ca", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700 }}>
                &#x1F517;
              </Box>
              Zoho CRM Fields / Default
            </Typography>
            <Box />
          </Box>

          {rows.map((row, i) => {
            const zohoField = zohoFields.find((f) => (f.api_name || f) === row.Zoho);
            const isLocked = row.locked === true || isExtensionOwnedZohoField(row.Zoho);
            const isRequired = !!zohoField?.system_mandatory;
            const sf = shopifyFields.find((f) => (f.api_name || f) === row.Shopify);
            const isSubformPair = isSubformLikeZohoField(zohoField) && sf?.type === "array";

            // Subform sub-fields: prefer fields the new POST endpoint returned
            // under the parent's group key (`_groupKey === row.Zoho`), with
            // their qualifying prefix stripped so the picker shows bare names
            // like "product", "quantity". Falls back to the legacy
            // subformFieldsCache for the SDK / hardcoded path.
            let subZohoFields = [];
            if (row.Zoho) {
              const prefix = `${row.Zoho}.`;
              subZohoFields = zohoFields
                .filter((f) => f._groupKey === row.Zoho)
                .map((f) => ({
                  ...f,
                  api_name:
                    typeof f.api_name === "string" && f.api_name.startsWith(prefix)
                      ? f.api_name.slice(prefix.length)
                      : f.api_name,
                  _group: undefined, // no nested grouping inside the sub-block
                }));
            }
            if (!subZohoFields.length) {
              subZohoFields = subformFieldsCache[row.Zoho]?.fields || [];
            }

            return (
              <React.Fragment key={i}>
                <MappingRow
                  row={row}
                  index={i}
                  shopifyFields={shopifyFields}
                  zohoFields={zohoFields}
                  allRows={rows}
                  onChange={onChange}
                  onSourceChange={onSourceChange}
                  onAdd={onAdd}
                  onRemove={onRemove}
                  isRequired={isRequired}
                  isLocked={isLocked}
                />
                {isSubformPair && (
                  <SubformBlock
                    parentRow={row}
                    parentIndex={i}
                    shopifyFields={shopifyFields}
                    subZohoFields={subZohoFields}
                    onSubChange={onSubChange}
                    onSubSourceChange={onSubSourceChange}
                    onSubAdd={onSubAdd}
                    onSubRemove={onSubRemove}
                  />
                )}
              </React.Fragment>
            );
          })}
        </>
      )}
    </Box>
  );
}

// ── Step 4: Actions ──
function ActionsStep({
  recordAction,
  setRecordAction,
  zohoFields,
  matchField,
  setMatchField,
  shopUrlField,
  setShopUrlField,
  enabled,
  setEnabled,
}) {
  const matchOptions = useMemo(() => {
    const isIdLike = (f) =>
      /(^|[^A-Za-z])ID([^A-Za-z]|$)|shopify[_ ]?id/i.test(f.api_name);
    return [...zohoFields].sort((a, b) => {
      const ai = isIdLike(a) ? 0 : 1;
      const bi = isIdLike(b) ? 0 : 1;
      return ai - bi;
    });
  }, [zohoFields]);

  // Shop URL Field options — surface fields whose name hints at "shop url"
  // first (e.g. easyshopifyforzohocrm__Shopify_Shop_URL), but allow any.
  const shopUrlOptions = useMemo(() => {
    const isShopUrlLike = (f) =>
      /shop[_ ]?url|shopify[_ ]?(shop|store)/i.test(f.api_name);
    return [...zohoFields].sort((a, b) => {
      const ai = isShopUrlLike(a) ? 0 : 1;
      const bi = isShopUrlLike(b) ? 0 : 1;
      return ai - bi;
    });
  }, [zohoFields]);

  const needsMatchField = recordAction !== "create";

  return (
    <Box sx={{ p: 3.5, pt: 2 }}>
      {/* 2-column grid keeps the form compact so Save Mapping stays in view */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          columnGap: 2,
          rowGap: 2,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#3f3f46", mb: "6px" }}>
            Record Action
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              value={recordAction}
              onChange={(e) => setRecordAction(e.target.value)}
              sx={{ fontSize: 14 }}
            >
              <MenuItem value="create" sx={{ fontSize: 14 }}>Create</MenuItem>
              <MenuItem value="upsert" sx={{ fontSize: 14 }}>Upsert (Create or Update)</MenuItem>
              <MenuItem value="update" sx={{ fontSize: 14 }}>Update only</MenuItem>
              <MenuItem value="delete" sx={{ fontSize: 14 }}>Delete</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#3f3f46", mb: "6px" }}>
            Shop URL Field{" "}
            <Typography component="span" sx={{ fontSize: 12, color: "#a1a1aa", fontWeight: 400 }}>
              (stores Shopify shop URL)
            </Typography>
          </Typography>
          <Autocomplete
            size="small"
            options={shopUrlOptions}
            getOptionLabel={(opt) => opt?.display_label || opt?.api_name || opt || ""}
            value={zohoFields.find((f) => (f.api_name || f) === shopUrlField) || null}
            onChange={(_, newVal) =>
              setShopUrlField(newVal ? newVal.api_name || newVal : "")
            }
            isOptionEqualToValue={(opt, val) => (opt.api_name || opt) === (val.api_name || val)}
            renderInput={(params) => <TextField {...params} placeholder="— Select Zoho field —" />}
          />
        </Box>

        {needsMatchField && (
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#3f3f46", mb: "6px" }}>
              Match Field{" "}
              <Typography component="span" sx={{ fontSize: 12, color: "#a1a1aa", fontWeight: 400 }}>
                (stores Shopify record ID)
              </Typography>
            </Typography>
            <Autocomplete
              size="small"
              options={matchOptions}
              getOptionLabel={(opt) => opt?.display_label || opt?.api_name || opt || ""}
              value={zohoFields.find((f) => (f.api_name || f) === matchField) || null}
              onChange={(_, newVal) =>
                setMatchField(newVal ? newVal.api_name || newVal : "")
              }
              isOptionEqualToValue={(opt, val) => (opt.api_name || opt) === (val.api_name || val)}
              renderInput={(params) => <TextField {...params} placeholder="— Select Zoho field —" />}
            />
          </Box>
        )}

        <Box sx={{ gridColumn: needsMatchField ? "auto" : "1 / -1" }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#3f3f46", mb: "6px" }}>
            Enable mapping{" "}
            <Typography component="span" sx={{ fontSize: 12, color: "#a1a1aa", fontWeight: 400 }}>
              (turn off to pause the trigger)
            </Typography>
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              border: "1px solid #e4e4e7",
              borderRadius: "4px",
              px: 1.25,
              height: 40,
            }}
          >
            <Typography sx={{ fontSize: 14, color: "#3f3f46" }}>
              {enabled ? "Enabled" : "Disabled"}
            </Typography>
            <Switch
              size="small"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

// ── Main page ──
export default function CustomAction() {
  const [loading, setLoading] = useState(true);
  const [extensionData, setExtensionData] = useState(null);
  const [entity, setEntity] = useState(null);

  // Wizard state
  const [currentStep, setCurrentStep] = useState(0);
  const [shop, setShop] = useState("");
  const [target, setTarget] = useState("");
  const [recordAction, setRecordAction] = useState("upsert");
  const [matchField, setMatchField] = useState(`${EXT}__Shopify_ID_EXT`);
  const [shopUrlField, setShopUrlField] = useState(`${EXT}__Shopify_Shop_URL`);
  const [enabled, setEnabled] = useState(true);
  const [rows, setRows] = useState([EMPTY_ROW()]);

  // Field metadata
  const [zohoFields, setZohoFields] = useState([]);
  // Fields arrive pre-fetched via initZohoCustomAction, so there's no async
  // load to track — kept as state (without a setter) for the step-3 spinner
  // prop it feeds.
  const [zohoFieldsLoading] = useState(false);
  const [stores, setStores] = useState([]);
  const [subformFieldsCache, setSubformFieldsCache] = useState({});

  // Persisted setting (if any) — populated when an existing config is loaded
  const [editingSettingId, setEditingSettingId] = useState("");
  const [saving, setSaving] = useState(false);

  // ── initZohoCustomAction — base auth + saved config + Zoho fields ──
  // initZohoCustomAction layers custom-action specific fetches on top of the
  // base data: ZOHO.CRM.ACTION.getConfig for the saved settingId, the saved
  // setting itself, and the entity's Zoho module fields. So we can prefill
  // from the callback's payload in one shot — no extra effects needed.
  useEffect(() => {
    initZohoCustomAction((error, data) => {
      if (error || !data) {
        setLoading(false);
        return;
      }
      setExtensionData(data);
      setEntity(data.entity || null);

      // Pre-fetched stores from initZoho's underlying fetchAllShopifyStores
      setStores(data.shopifyStores || []);

      // Pre-fetched Zoho module fields for the entity
      setZohoFields(data.zohoFields || []);

      // Saved setting → prefill the wizard for editing
      const saved = data.savedSetting;
      const schema = saved?.settingSchema || {};
      if (data.savedSettingId && schema && Object.keys(schema).length) {
        setEditingSettingId(data.savedSettingId);
        setShop(schema.shop || "");
        setTarget( schema.target || DEFAULT_TARGET_BY_MODULE[data.entity] || "" );
        setRecordAction(schema.recordAction || "upsert");
        setMatchField(schema.matchField || `${EXT}__Shopify_ID_EXT`);
        setShopUrlField(schema.shopUrlField || `${EXT}__Shopify_Shop_URL`);
        setEnabled(schema.enabled !== false);
        setRows( Array.isArray(schema.rows) && schema.rows.length ? 
            schema.rows.map(normaliseRow) : [ EMPTY_ROW() ]
        );
      }
      // Fresh setup: leave Shop Name and Target Resource empty so the user
      // has to pick them explicitly on step 2 — no silent defaults.

      setLoading(false);
    });
  }, []);

  const settingAuth = useMemo(
    () =>
      extensionData
        ? {
            orgId: extensionData.orgId,
            apiKey: extensionData.apiKey,
            dataCenterUrl: extensionData.dataCenterUrl,
          }
        : null,
    [extensionData]
  );

  // Shopify field list for the chosen target. Maps the lowercase resource
  // key the wizard uses to the SHOPIFY_FIELDS_MAP key.
  const shopifyFields = useMemo(() => {
    if (!target) return [];
    const key = SHOPIFY_FIELDS_MAP_KEY[target] || target;
    return SHOPIFY_FIELDS_MAP[key] || [];
  }, [target]);

  // Pre-warm the subform cache for every subform parent currently in the
  // mapping. Covers two cases: (1) prefilled rows from a saved config and
  // (2) freshly-added rows where the user just picked a subform Zoho field.
  // ensureSubformLoaded is idempotent so re-firing this effect is cheap.
  useEffect(() => {
    if (!zohoFields?.length || !rows?.length) return;
    rows.forEach((r) => {
      if (!r?.Zoho) return;
      const zf = zohoFields.find((f) => (f.api_name || f) === r.Zoho);
      if (isSubformLikeZohoField(zf)) ensureSubformLoaded(zf);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, zohoFields]);

  // Subform sub-field loader — fetches the child module's fields the first
  // time a subform parent is picked, caches by parent api_name.
  // Lazily load Zoho subform sub-fields for a parent. Uses inline metadata
  // when Zoho returned it on the parent field, falls back to the hardcoded
  // map for built-in subforms (e.g. Product_Details), and as a last resort
  // calls getZohoModuleFields(subModule). Idempotent — once cached, never
  // re-fetched.
  const ensureSubformLoaded = async (zohoField) => {
    const apiName = zohoField?.api_name;
    if (!apiName) return;
    if (subformFieldsCache[apiName]) return;

    const ctx = getSubformContext(zohoField);
    if (!ctx) return;

    // Inline metadata takes priority — no API call needed.
    if (ctx.inlineFields?.length) {
      setSubformFieldsCache((p) => ({
        ...p,
        [apiName]: { loading: false, fields: ctx.inlineFields },
      }));
      return;
    }

    // Hardcoded fallback for built-in subforms whose sub-fields Zoho's API
    // doesn't surface (Product_Details is the canonical example).
    if (!ctx.moduleApiName && ctx.fallbackFields?.length) {
      setSubformFieldsCache((p) => ({
        ...p,
        [apiName]: { loading: false, fields: ctx.fallbackFields },
      }));
      return;
    }

    if (!ctx.moduleApiName) {
      // No way to resolve sub-fields — surface the hardcoded list if any.
      setSubformFieldsCache((p) => ({
        ...p,
        [apiName]: { loading: false, fields: ctx.fallbackFields || [] },
      }));
      return;
    }

    setSubformFieldsCache((p) => ({
      ...p,
      [apiName]: { loading: true, fields: [] },
    }));
    try {
      const fields = await getZohoModuleFields(window.ZOHO, ctx.moduleApiName);
      const resolved =
        Array.isArray(fields) && fields.length
          ? fields
          : ctx.fallbackFields || [];
      setSubformFieldsCache((p) => ({
        ...p,
        [apiName]: { loading: false, fields: resolved },
      }));
    } catch (err) {
      console.error("[CustomAction] subform fields fetch failed:", err);
      setSubformFieldsCache((p) => ({
        ...p,
        [apiName]: { loading: false, fields: ctx.fallbackFields || [] },
      }));
    }
  };

  // ── Save: upsertSetting → ZOHO.CRM.ACTION.setConfig ──
  const handleSave = async () => {
    if (!settingAuth) return;
    setSaving(true);
    try {
      const settingSchema = {
        shop,
        module: entity,
        target,
        recordAction,
        matchField: recordAction === "create" ? "" : matchField,
        shopUrlField,
        enabled,
        service: "Zoho CRM",
        direction: "zoho_to_shopify",
        rows,
      };

      const result = await upsertSetting(settingAuth, {
        settingId: editingSettingId || "",
        settingSchema,
        moduleApiName: entity || "",
        settingType: "customAction",
      });

      const newSettingId =
        result?.settingId || result?.setting_id || result?.id || editingSettingId || "";

      if (newSettingId !== "") {
        await ZOHO.CRM.ACTION.setConfig({
          settingId: newSettingId,
          orgid: extensionData.orgId,
          datacenterurl: extensionData.dataCenterUrl,
          datacenter: extensionData.dataCenter,
          shopifyshopurl: shop,
          apikey: extensionData.apiKey,
          connname: EXT,
        });
        setEditingSettingId(newSettingId);
      }

      ZOHO?.CRM?.UI?.Popup?.closeReload?.();
    } catch (err) {
      console.error("[CustomAction] save error:", err);
    }
    setSaving(false);
  };

  // ── Per-step validation ──
  // Each step is "complete" when its required inputs are filled. The user
  // can't navigate beyond a step that isn't complete, and Next is disabled
  // on an incomplete step.
  const isStepComplete = (i) => {
    if (i === 0) return !!entity; // Zoho App: just needs the entity from initZoho
    if (i === 1) return !!shop && !!target; // Shopify: shop + target required
    if (i === 2) {
      // Field Mapping: at least one fully-filled row
      return rows.some((r) => {
        if (!r.Shopify) return false;
        if (r.default) return r.defaultValue !== "" && r.defaultValue != null;
        return !!r.Zoho;
      });
    }
    if (i === 3) {
      // Actions: shop URL field always required; match field required for non-create
      if (!shopUrlField) return false;
      if (recordAction !== "create" && !matchField) return false;
      return true;
    }
    return true;
  };

  // Highest step the user can jump to: walk from 0 until we hit the first
  // incomplete step, and allow one step past it (the next step the user
  // needs to fill). Result is clamped to [0, STEPS.length - 1].
  const maxReachable = useMemo(() => {
    for (let i = 0; i < STEPS.length; i++) {
      if (!isStepComplete(i)) return i;
    }
    return STEPS.length - 1;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity, shop, target, rows, recordAction, matchField, shopUrlField]);

  const canAdvance = isStepComplete(currentStep);

  const next = () => {
    if (!canAdvance) return;
    setCurrentStep((s) => Math.min(s + 1, STEPS.length - 1));
  };
  const back = () => setCurrentStep((s) => Math.max(s - 1, 0));

  const safeStepClick = (i) => {
    if (i <= maxReachable) setCurrentStep(i);
  };

  // ── Render guards ──
  if (loading) return <Loader />;
  if (!extensionData?.orgId || !extensionData?.apiKey || !extensionData?.dataCenterUrl) {
    return <ShowError />;
  }
  if (!extensionData?.shopifyAuth || !extensionData?.zohoAuth) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", p: 3 }}>
        <Box sx={{ textAlign: "center", color: "#71717a" }}>
          <Box sx={{ fontSize: 48, mb: 2 }}>🔒</Box>
          <Box sx={{ fontSize: 16, fontWeight: 600, color: "#3f3f46", mb: 1 }}>
            Authentication Required
          </Box>
          <Box sx={{ fontSize: 14 }}>
            {!extensionData?.shopifyAuth && !extensionData?.zohoAuth
              ? "Please authenticate both Shopify and Zoho CRM in the Settings page."
              : !extensionData?.shopifyAuth
              ? "Please authenticate Shopify in the Settings page."
              : "Please authenticate Zoho CRM in the Settings page."}
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <>
      {editingSettingId && <SavedSettingBanner settingId={editingSettingId} />}
      <Box
        sx={{
          maxWidth: 900,
          mx: "auto",
          my: 4,
          bgcolor: "#fff",
          border: "1px solid #e4e4e7",
          borderRadius: "10px",
          overflow: "hidden",
          boxShadow: "0 1px 3px 0 rgba(0,0,0,.04)",
          // The Zoho popup page never scrolls (App.css locks #root), so cap the
          // card to the viewport and lay it out as a column: header, stepper
          // and footer stay pinned while the step panel scrolls internally.
          // The Setting-ID banner (edit mode) sits above the card, so leave it
          // extra room when present.
          display: "flex",
          flexDirection: "column",
          maxHeight: editingSettingId
            ? "calc(100vh - 150px)"
            : "calc(100vh - 64px)",
        }}
      >
      <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between", px: 3.5, pt: 2.5, pb: 0.5 }}>
        <Typography sx={{ fontSize: 17, fontWeight: 700 }}>
          {editingSettingId ? "Edit Field Mapping" : "Add Field Mapping"}
        </Typography>
        <IconButton size="small" onClick={() => ZOHO?.CRM?.UI?.Popup?.close?.()}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ flexShrink: 0 }}>
        <StepIndicator
          currentStep={currentStep}
          onStepClick={safeStepClick}
          maxReachable={maxReachable}
        />
      </Box>

      {/* Step panel — the only scrollable region */}
      <Box sx={{ borderTop: "1px solid #f4f4f5", flex: 1, minHeight: 0, overflowY: "auto" }}>
        {currentStep === 0 && <ZohoAppStep moduleName={entity} />}
        {currentStep === 1 && (
          <ShopifyStep
            shop={shop}
            setShop={setShop}
            target={target}
            setTarget={setTarget}
            stores={stores}
          />
        )}
        {currentStep === 2 && (
          <FieldMappingStep
            moduleName={entity}
            shop={shop}
            target={target}
            rows={rows}
            setRows={setRows}
            zohoFields={zohoFields}
            shopifyFields={shopifyFields}
            zohoFieldsLoading={zohoFieldsLoading}
            subformFieldsCache={subformFieldsCache}
            ensureSubformLoaded={ensureSubformLoaded}
          />
        )}
        {currentStep === 3 && (
          <ActionsStep
            recordAction={recordAction}
            setRecordAction={setRecordAction}
            zohoFields={zohoFields}
            matchField={matchField}
            setMatchField={setMatchField}
            shopUrlField={shopUrlField}
            setShopUrlField={setShopUrlField}
            enabled={enabled}
            setEnabled={setEnabled}
          />
        )}
      </Box>

      <Box
        sx={{
          flexShrink: 0,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 3.5,
          py: 2,
          borderTop: "1px solid #f4f4f5",
        }}
      >
        <Button
          variant="outlined"
          size="small"
          onClick={() => ZOHO?.CRM?.UI?.Popup?.close?.()}
          sx={{ textTransform: "none", color: "#3f3f46", borderColor: "#e4e4e7" }}
        >
          Cancel
        </Button>
        <Box sx={{ display: "flex", gap: 1 }}>
          {currentStep > 0 && (
            <Button
              variant="outlined"
              size="small"
              onClick={back}
              sx={{ textTransform: "none", color: "#3f3f46", borderColor: "#e4e4e7" }}
            >
              &larr; Back
            </Button>
          )}
          {currentStep < STEPS.length - 1 ? (
            <Button
              variant="contained"
              size="small"
              onClick={next}
              disabled={!canAdvance}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                bgcolor: "#09090b",
                "&:hover": { bgcolor: "#27272a" },
                "&.Mui-disabled": { bgcolor: "#e4e4e7", color: "#a1a1aa" },
              }}
            >
              Next &rarr;
            </Button>
          ) : (
            <Button
              variant="contained"
              size="small"
              onClick={handleSave}
              disabled={saving || !canAdvance}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                bgcolor: "#16a34a",
                "&:hover": { bgcolor: "#15803d" },
                "&.Mui-disabled": { bgcolor: "#e4e4e7", color: "#a1a1aa" },
              }}
            >
              {saving ? <CircularProgress size={14} sx={{ mr: 1, color: "#fff" }} /> : null}
              {editingSettingId ? "Update Mapping" : "Save Mapping"}
            </Button>
          )}
        </Box>
      </Box>
      </Box>
    </>
  );
}
